import React, { useState } from 'react'
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props'
import axios from 'axios'
import { useRouter } from 'next/router'
import useTrans from './useTrans'
import useLocalStorage from './useLocalStorage'
import { useGoogleLogin } from '@react-oauth/google'
import SocialButton from '../components/atoms/SocialButton/SocialButton'
import { SOCIAL_LOGIN } from '../utils/endpoint'
import { api } from '../utils'
import { displayNotification } from '../components/atoms/Notifica/Notifica'
import { isSocialLoginAllowed } from '../utils/servicegift'

export const CODE_ERROR_USER_NOT_ACTIVE = 'user_not_active'
export const CODE_ERROR_NO_ACCOUNT_FOUND = 'no_account_found'

function useSocialLogin(service_gift, redirectUrl) {
  const router = useRouter()
  const t = useTrans()

  const socialLogin = null
  const loading = null
  const [error, setError] = useState(null)
  const [visibilityButtonGoogle, setVisibilityButtonGoogle] = useState(true)
  const [, setToken] = useLocalStorage('token', null)
  const [, setUserId] = useLocalStorage('userId', null)

  async function handleSocialLogin(provider, accessToken, profileData) {
    if (error) setError(null)
    const res = await api
      .post(`${SOCIAL_LOGIN}${provider + '/'}`, {
        access_token: accessToken,
      })
      .then(async (res) => {
        displayNotification('success.login', t('Hai eseguito il login con successo', 'success'))
        setError(null)
        window.location.href = redirectUrl || '/'
      })
      .catch((err) => {
        if (err?.response?.data?.code === CODE_ERROR_NO_ACCOUNT_FOUND) {
          setError('Utente non registrato')
          router.push(
            `/myaccount/registrazione?social=true&nome=${profileData?.nome}&cognome=${profileData?.cognome}&email=${profileData?.email}`
          )
        } else if (err?.response?.data?.code === CODE_ERROR_USER_NOT_ACTIVE)
          setError('Utente non abilitato')
        else setError('Si è verificato un errore')
      })
  }

  async function handleGoogleLogin(provider, responseData) {
    if (error) setError(null)
    // The access_token is obtained on the backend side.
    const res = await api
      .post(`${SOCIAL_LOGIN}${provider + '/'}`, {
        google_auth_data: responseData,
      })
      .then(async (res) => {
        displayNotification('success.login', t('Hai eseguito il login con successo', 'success'))
        setError(null)
        window.location.href = redirectUrl || '/'
      })
      .catch((err) => {
        if (
          err?.response?.data?.code === CODE_ERROR_NO_ACCOUNT_FOUND &&
          err?.response?.data?.social_data
        ) {
          const data = {
            nome: err.response.data.social_data?.nome || '',
            cognome: err.response.data.social_data?.cognome || '',
            email: err.response.data.social_data?.email || '',
          }
          setError('Utente non registrato')
          router.push(
            `/myaccount/registrazione?social=true&nome=${data?.nome}&cognome=${data?.cognome}&email=${data?.email}`
          )
        } else if (err?.response?.data?.code === CODE_ERROR_USER_NOT_ACTIVE)
          setError('Utente non abilitato')
        else setError('Si è verificato un errore')
      })
  }

  function prepareFacebookLogin(response) {
    if (response.status) {
      if (response.status !== 'unknown') setError(t('Si è verificato un errore'))
    } else if (response.accessToken) {
      const data = {
        nome: response.first_name || '',
        cognome: response.last_name || '',
        email: response.email || '',
      }
      handleSocialLogin('facebook', response?.accessToken, data)
    }
  }

  function prepareGoogleLogin(response) {
    if (response.error) {
      if (response.error !== 'popup_closed_by_user') setError('Si è verificato un errore')
    } else if (response) {
      handleGoogleLogin('google-oauth2', response)
    }
  }

  function failureGoogleLogin(response) {
    if (response?.error === 'idpiframe_initialization_failed') {
      setVisibilityButtonGoogle(false)
    } else setError('Si è verificato un errore')
  }

  const loginGoogle =
    isSocialLoginAllowed(service_gift) && service_gift?.social_auth_google_oauth2_key
      ? useGoogleLogin({
          onSuccess: (codeResponse) => prepareGoogleLogin(codeResponse),
          onError: (codeResponse) => failureGoogleLogin(codeResponse),
          scope: 'profile email',
          flow: 'auth-code',
        })
      : () => null

  const FacebookLoginButton = () =>
    isSocialLoginAllowed(service_gift) && service_gift?.social_auth_facebook_key ? (
      <FacebookLogin
        isMobile={false}
        appId={service_gift?.social_auth_facebook_key ? service_gift.social_auth_facebook_key : ''}
        callback={prepareFacebookLogin}
        fields="first_name,last_name,email"
        render={(renderProps) => (
          <SocialButton
            social="facebook"
            className="login__social-button"
            onClick={renderProps.onClick}
            label={t('Facebook')}
          />
        )}
      />
    ) : null

  const GoogleLoginButton = () =>
    visibilityButtonGoogle &&
    isSocialLoginAllowed(service_gift) &&
    service_gift?.social_auth_google_oauth2_key ? (
      <SocialButton
        social="google"
        className="login__social-button"
        onClick={() => loginGoogle()}
        label={t('Google')}
      />
    ) : null

  return { FacebookLoginButton, GoogleLoginButton, socialLogin, error, loading }
}

export default useSocialLogin
